import { motion } from "framer-motion";
import { Link } from "react-router-dom";

// Production home hero — Vault Door composition (already-open reveal)
export default function HeroVault() {
  return (
    <section
      className="relative min-h-[100svh] pt-28 pb-16 overflow-hidden bg-[#030303]"
      data-testid="hero"
    >
      {/* corner markings */}
      <div className="absolute top-28 left-6 md:left-10 overline text-[#666] z-30">Ξ 001 · VAULT SEQUENCE</div>
      <div className="absolute top-28 right-6 md:right-10 overline text-[#666] z-30">CLEARANCE · AU</div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-[76svh] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="overline text-[#666] mb-4"
        >
          — ACCESS GRANTED —
        </motion.div>

        {/* Vault composition — flanking slab-panels on left and right, central reveal in middle */}
        <div className="relative w-full max-w-[1200px] h-[52vh] md:h-[60vh] grid grid-cols-[minmax(40px,1fr)_minmax(0,7fr)_minmax(40px,1fr)]">
          {/* LEFT SLAB (parked, already retracted) */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative border border-[#1A1A1A] bg-[#050505] flex items-center justify-end pr-3 md:pr-6 overflow-hidden"
          >
            <div
              className="absolute inset-0"
              style={{ backgroundImage: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.02) 60%, transparent 100%)" }}
            />
            <div className="relative flex flex-col items-end gap-3 text-right">
              <div className="overline text-[#888] hidden md:block">LEFT SLAB</div>
              <div className="font-mono text-[#444] text-xs hidden md:block">33.8° S</div>
              <motion.div
                initial={{ rotate: 0 }}
                animate={{ rotate: 360 }}
                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                className="w-10 h-10 md:w-14 md:h-14 border border-[#333] flex items-center justify-center"
              >
                <div className="w-5 md:w-7 h-5 md:h-7 border border-[#7C3AED] rotate-45" />
              </motion.div>
            </div>
          </motion.div>

          {/* CENTRAL VAULT REVEAL */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative border-y border-[#1A1A1A] bg-[#080808] flex flex-col items-center justify-center overflow-hidden"
          >
            {/* red seam pulse on both edges */}
            <motion.div
              initial={{ scaleY: 0, opacity: 0 }}
              animate={{ scaleY: 1, opacity: [0, 0.9, 0.6, 0.9, 0.6] }}
              transition={{
                scaleY: { duration: 0.5, delay: 0.1 },
                opacity: { duration: 4, delay: 0.1, repeat: Infinity, ease: "easeInOut" },
              }}
              className="absolute inset-y-0 left-0 w-px bg-[#7C3AED]"
              style={{ transformOrigin: "center" }}
            />
            <motion.div
              initial={{ scaleY: 0, opacity: 0 }}
              animate={{ scaleY: 1, opacity: [0, 0.9, 0.6, 0.9, 0.6] }}
              transition={{
                scaleY: { duration: 0.5, delay: 0.2 },
                opacity: { duration: 4, delay: 0.2, repeat: Infinity, ease: "easeInOut" },
              }}
              className="absolute inset-y-0 right-0 w-px bg-[#7C3AED]"
              style={{ transformOrigin: "center" }}
            />

            {/* dot grid backdrop */}
            <div
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
                backgroundSize: "22px 22px",
              }}
            />

            {/* ninja watermark */}
            <motion.img
              src="https://customer-assets-cm19k8pv.emergentagent.net/job_degoogled-oz/artifacts/c5gb5oy4_masked-hooded-ninja-assassin-warrior-emblem-vector-51649204%20%281%29.webp"
              alt=""
              aria-hidden="true"
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 0.08, scale: 1 }}
              transition={{ duration: 2.4, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 m-auto h-[80%] w-auto object-contain pointer-events-none"
              style={{ filter: "invert(1) grayscale(1) contrast(1.2)" }}
            />

            <div className="relative z-10 text-center px-6">
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="font-phantom uppercase text-5xl md:text-[7vw] leading-[0.9] tracking-[0.02em]"
                data-testid="hero-heading"
              >
                Phantom<br />
                <span className="text-[#7C3AED]">Phone.</span>
              </motion.h1>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.7 }}
                className="mt-6 md:mt-8 overline"
              >
                GrapheneOS · Australian owned · Zero telemetry
              </motion.div>
            </div>

            {/* corner ticks */}
            <div className="absolute top-3 left-3 w-4 h-4 border-l border-t border-[#333]" />
            <div className="absolute top-3 right-3 w-4 h-4 border-r border-t border-[#333]" />
            <div className="absolute bottom-3 left-3 w-4 h-4 border-l border-b border-[#333]" />
            <div className="absolute bottom-3 right-3 w-4 h-4 border-r border-b border-[#333]" />
          </motion.div>

          {/* RIGHT SLAB */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative border border-[#1A1A1A] bg-[#050505] flex items-center justify-start pl-3 md:pl-6 overflow-hidden"
          >
            <div
              className="absolute inset-0"
              style={{ backgroundImage: "linear-gradient(-90deg, transparent 0%, rgba(255,255,255,0.02) 60%, transparent 100%)" }}
            />
            <div className="relative flex flex-col items-start gap-3">
              <div className="overline text-[#888] hidden md:block">RIGHT SLAB</div>
              <div className="font-mono text-[#444] text-xs hidden md:block">SEAL # Ξ</div>
              <motion.div
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                className="w-10 h-10 md:w-14 md:h-14 border border-[#333] flex items-center justify-center"
              >
                <div className="tier-dot phantom scale-[2.5]" />
              </motion.div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9, duration: 0.9 }}
          className="mt-10 flex flex-col md:flex-row items-center gap-4 md:gap-6"
        >
          <Link to="/shop" className="btn-vault danger" data-testid="hero-cta-shop">
            Enter the vault
          </Link>
          <Link to="/manifesto" className="btn-ghost" data-testid="hero-cta-manifesto">
            Read the doctrine →
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 0.6 }}
          className="mt-10 grid grid-cols-3 gap-8 md:gap-20 text-center"
        >
          {[["7", "Devices"], ["3", "Tiers"], ["0", "Telemetry"]].map(([n, l]) => (
            <div key={l}>
              <div className="font-display text-3xl md:text-5xl">{n}</div>
              <div className="overline mt-2">{l}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="absolute bottom-6 left-6 md:left-10 overline text-[#666] z-30 flex items-center gap-3">
        <span className="tier-dot phantom blink" /> Biometric match
      </div>
      <div className="absolute bottom-6 right-6 md:right-10 overline text-[#666] z-30">Hand hardened · Sydney</div>
    </section>
  );
}
